/**
 * #731 — keep an unchanged row's view-model at the SAME object reference.
 *
 * The Position Tracker rebuilds every order/position view-model on each market
 * publication (4 Hz). At the observed production shape — 858 open orders — that
 * is ~3,400 fresh objects per second whose contents are, for all but the few
 * rows whose market actually moved, byte-identical to the ones they replace.
 * Every one of those new references invalidates its row.
 *
 * This is the "reshape the state so the UI does not needlessly invalidate"
 * half of the ticket, and it is deliberately NOT a cache: nothing is withheld,
 * nothing is delayed, and no value is served stale. A freshly built view-model
 * is compared against the previous one BY VALUE; when they are equal the older
 * reference is handed back because the two are interchangeable. When anything
 * differs, the new one wins immediately.
 *
 * ### The rule that keeps this honest
 *
 * **Unknown compares as CHANGED.** If `sameVm` cannot confidently prove two
 * view-models equal — a field it cannot compare, a shape it does not expect,
 * differing key sets — it reports them different and the row re-renders. The
 * failure this ticket can most easily cause is a row that skips a render it
 * owed, showing a stale price on a trading screen; that is worse than the
 * slowness being fixed. So every uncertainty resolves toward MORE rendering.
 *
 * ### It is mutated during render, and that is safe here
 *
 * `stabilize()` is called from a `useMemo` and updates the store as it goes,
 * so the obvious objection is that a render-phase side effect is impure. The
 * property that makes it safe is IDEMPOTENCE: calling it twice with equal
 * input returns the identical array both times, because the second pass
 * compares against what the first pass chose and `sameVm` proves them equal.
 *
 * That matters concretely. The app renders inside `React.StrictMode`
 * (`src/main.tsx`), so every render is double-invoked in development, and
 * under concurrent rendering React may abandon a render outright. In the
 * abandoned case the store holds objects chosen during a discarded pass — but
 * since reuse requires VALUE equality, the worst outcome is reusing a
 * reference whose contents are identical to the one it replaced. No wrong
 * value can be shown, and no row can skip a render it owed.
 *
 * Covered by "returns the SAME array when no row moved", which calls
 * `stabilize` twice across a mutation of the store's own state.
 *
 * ### Why the comparison is derived, not hand-written
 *
 * The field list is read off the objects themselves rather than enumerated
 * here. A hand-written list is a latent staleness bug: the day someone adds a
 * 37th field to `OrderCardVM`, an enumerated comparison silently stops noticing
 * it and that field freezes on screen. Deriving the keys means a new field is
 * covered the moment it exists.
 */

/** Identity is the row's own unique key — never a grouping attribute. */
interface Keyed {
  key: string;
}

/**
 * Value equality for one view-model.
 *
 * Compares own enumerable keys, one level deep into plain objects (the
 * `PriceQuote` pairs are the only nesting these view-models carry). Anything
 * else — functions, arrays, Maps, deeper nesting, mismatched key sets —
 * reports UNEQUAL rather than guessing.
 */
export function sameVm(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (!isPlainRecord(a) || !isPlainRecord(b)) return false;

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;

  for (const k of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
    const av = a[k];
    const bv = b[k];
    if (Object.is(av, bv)) continue;
    // One level of nesting is expected and comparable; anything below it is
    // not, so it counts as changed.
    if (isPlainRecord(av) && isPlainRecord(bv)) {
      if (!shallowRecordEqual(av, bv)) return false;
      continue;
    }
    return false;
  }
  return true;
}

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  if (v === null || typeof v !== 'object') return false;
  if (Array.isArray(v)) return false;
  const proto = Object.getPrototypeOf(v) as unknown;
  return proto === Object.prototype || proto === null;
}

/** Primitives only. A nested object inside a nested object reports unequal. */
function shallowRecordEqual(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  for (const k of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
    const av = a[k];
    const bv = b[k];
    if (Object.is(av, bv)) continue;
    if (typeof av === 'object' && av !== null) return false;
    return false;
  }
  return true;
}

/**
 * A per-row identity store. `stabilize` takes the freshly built list and
 * returns one in which every value-identical row keeps its previous reference.
 *
 * Rows that disappear are dropped from the store on the same pass, so a long
 * session cannot accumulate the identities of orders that are long gone.
 */
export function createVmIdentityStore<T extends Keyed>() {
  return createKeyedIdentityStore<T>((vm) => vm.key);
}

/**
 * The same store for values that carry their identity somewhere other than a
 * `key` field — the sorted table ROWS, for instance, whose id comes from
 * `orderRowId(row)`. Those wrappers are rebuilt on every publication even when
 * the order inside them is untouched, and a fresh wrapper invalidates its row
 * just as surely as a fresh view-model does.
 */
export function createKeyedIdentityStore<T>(keyOf: (v: T) => string) {
  let previous = new Map<string, T>();
  let previousList: T[] = [];

  return {
    stabilize(next: readonly T[]): T[] {
      const kept = new Map<string, T>();
      const out: T[] = [];
      for (const vm of next) {
        const before = previous.get(keyOf(vm));
        const use = before !== undefined && sameVm(before, vm) ? before : vm;
        kept.set(keyOf(vm), use);
        out.push(use);
      }
      previous = kept;

      /**
       * ⚠️ The ARRAY's identity matters as much as the rows'.
       *
       * Stabilizing each row but returning a fresh array still invalidates
       * everything downstream that depends on the list — and measured on this
       * page that was ruinous: the array feeds `gameKeyByVmKey`, which feeds
       * `sameGameProps`, which every row's handler bundle depends on. One new
       * array reference per tick therefore rebuilt the handlers for all 858
       * rows and defeated the entire optimisation, with every individual row
       * object correctly unchanged.
       *
       * Same rule as `sameVm`: reuse only when provably interchangeable —
       * identical length AND every element identity-equal, order included.
       */
      if (
        previousList.length === out.length &&
        out.every((vm, i) => Object.is(previousList[i], vm))
      ) {
        return previousList;
      }
      previousList = out;
      return out;
    },
    /** Rows currently held. Exposed so a test can prove rows are released. */
    size(): number {
      return previous.size;
    },
  };
}
